import { useEffect, useState } from 'react';
import { Trash2, Sparkles, Plus, Search, Calendar } from 'lucide-react';
import api from '@/lib/api';

const MOOD_TAGS = ['calm', 'anxious', 'tired', 'energized', 'sad', 'grateful', 'stressed'];

function formatDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Journal() {
    const [entries, setEntries] = useState([]);
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [content, setContent] = useState('');
    const [tags, setTags] = useState([]);
    const [prompts, setPrompts] = useState([]);
    const [loadingPrompts, setLoadingPrompts] = useState(false);
    const [chosenPrompt, setChosenPrompt] = useState('');
    const [saving, setSaving] = useState(false);

    const refresh = async () => {
        try {
            const r = await api.get('/journal', { params: q ? { q } : {} });
            setEntries(r.data || []);
        } catch {}
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [q]);

    const fetchPrompts = async () => {
        setLoadingPrompts(true);
        try {
            const { data } = await api.get('/journal/prompts');
            setPrompts(data.prompts || []);
        } catch {} finally { setLoadingPrompts(false); }
    };

    const startNew = () => {
        setOpen(true);
        setContent(''); setTags([]); setChosenPrompt('');
        if (prompts.length === 0) fetchPrompts();
    };

    const toggleTag = (t) => setTags((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);

    const save = async () => {
        if (!content.trim()) return;
        setSaving(true);
        try {
            await api.post('/journal', { content: content.trim(), mood_tags: tags, prompt: chosenPrompt || null });
            setOpen(false);
            refresh();
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this entry?')) return;
        await api.delete(`/journal/${id}`);
        refresh();
    };

    return (
        <div className="space-y-8" data-testid="journal-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">Journal</p>
                    <h1 className="font-serif text-4xl md:text-5xl text-forest mt-2">Quietly, in your own words</h1>
                    <p className="text-forest-soft mt-2 max-w-xl">A private space to land. No streaks. No judgement.</p>
                </div>
                <button onClick={startNew} data-testid="journal-new-button" className="qm-btn-primary inline-flex items-center gap-2 self-start">
                    <Plus size={16} /> New entry
                </button>
            </header>

            <div className="qm-card p-3 flex items-center gap-2" data-testid="journal-search">
                <Search size={16} className="text-forest-soft ml-2" />
                <input
                    type="search"
                    value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Search your entries…"
                    className="flex-1 bg-transparent outline-none text-forest placeholder:text-forest/40 py-2"
                    data-testid="journal-search-input"
                />
            </div>

            {open && (
                <div className="qm-card p-7 md:p-9 animate-fade-in" data-testid="journal-editor">
                    <div className="flex items-start justify-between gap-4 mb-4">
                        <h2 className="font-serif text-2xl text-forest">A quiet reflection</h2>
                        <button onClick={() => setOpen(false)} className="qm-btn-ghost text-sm" data-testid="journal-cancel">Cancel</button>
                    </div>

                    <div className="mb-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-forest-soft">A few prompts to begin (optional)</span>
                            <button onClick={fetchPrompts} disabled={loadingPrompts} data-testid="journal-refresh-prompts" className="text-xs text-forest underline disabled:opacity-50 inline-flex items-center gap-1">
                                <Sparkles size={12} /> {loadingPrompts ? 'Listening…' : 'Suggest more'}
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2" data-testid="journal-prompt-list">
                            {prompts.map((p, i) => (
                                <button
                                    key={i}
                                    onClick={() => { setChosenPrompt(p); if (!content) setContent(p + '\n\n'); }}
                                    data-testid={`journal-prompt-${i}`}
                                    className={`text-left text-sm rounded-2xl border px-4 py-2 transition ${chosenPrompt === p ? 'bg-sage/40 border-forest' : 'border-border hover:bg-sage/20'}`}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>

                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Write whatever comes up. No one will read this but you."
                        rows={8}
                        className="qm-input resize-y"
                        data-testid="journal-content-input"
                    />

                    <div className="mt-4">
                        <div className="text-sm text-forest-soft mb-2">Mood tags</div>
                        <div className="flex flex-wrap gap-2" data-testid="journal-tags">
                            {MOOD_TAGS.map((t) => (
                                <button
                                    key={t}
                                    onClick={() => toggleTag(t)}
                                    data-testid={`journal-tag-${t}`}
                                    className={`qm-pill text-sm ${tags.includes(t) ? 'bg-forest text-cream border-forest' : 'border-forest/20 hover:bg-sage/30'}`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button onClick={save} disabled={saving || !content.trim()} data-testid="journal-save-button" className="qm-btn-primary mt-6 disabled:opacity-50">
                        {saving ? 'Settling …' : 'Save entry'}
                    </button>
                </div>
            )}

            <ul className="space-y-4" data-testid="journal-entries">
                {entries.length === 0 && !open && (
                    <li className="qm-card p-10 text-center text-forest-soft" data-testid="journal-empty">
                        Your journal is quiet for now. When you’re ready, write something small.
                    </li>
                )}
                {entries.map((e) => (
                    <li key={e.id} className="qm-card p-6" data-testid={`journal-entry-${e.id}`}>
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-sm text-forest-soft inline-flex items-center gap-2">
                                <Calendar size={14} /> {formatDate(e.created_at)}
                            </div>
                            <button onClick={() => remove(e.id)} aria-label="Delete entry"
                                    data-testid={`journal-delete-${e.id}`}
                                    className="rounded-full w-9 h-9 grid place-items-center text-forest hover:bg-dusty/40">
                                <Trash2 size={15} />
                            </button>
                        </div>
                        {e.prompt && <div className="text-xs italic text-forest-soft mt-2">Prompt — {e.prompt}</div>}
                        <p className="font-serif text-forest mt-3 whitespace-pre-wrap">{e.content}</p>
                        {e.mood_tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3">
                                {e.mood_tags.map((t) => (
                                    <span key={t} className="qm-pill text-xs bg-sage/20 border-transparent">{t}</span>
                                ))}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
